import Text from '@/components/Text';
import { cn } from '@/lib/cn';
import { faInfoCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from 'components/tooltip/Tooltip';
import PluginIcon from 'pages/scope/PluginIcon';
import { useAvailableDataStreamDefinitionsForWorkspace } from 'queries/hooks/useAvailableDataStreamDefinitionsForWorkspace';
import { useRef } from 'react';
import { useSplashScreenDataStreamSelect } from '../hooks/useSplashScreenDataStreamSelect';
import { TileEditorDataStreams } from '../state/useTileEditorDataStreams';
import { SplashScreenDatasourceErrorText } from './SplashScreenDatasourceErrorText';
import { SplashScreenDataSource } from './useSplashScreenDatasources';

interface SplashScreenDatasourceButtonProps extends SplashScreenDataSource {
    tooltipText?: React.ReactNode;
    dataStreams: NonNullable<ReturnType<typeof useAvailableDataStreamDefinitionsForWorkspace>['data']>;
    tileEditorDataStreams: TileEditorDataStreams;
}

export const SplashScreenDatasourceButton: React.FC<SplashScreenDatasourceButtonProps> = ({
    id,
    name,
    tooltipText,
    dataStreams,
    tileEditorDataStreams,
    failedImports
}) => {
    const { selectDatasource } = useSplashScreenDataStreamSelect(id, dataStreams, tileEditorDataStreams);

    const disabled = failedImports && failedImports.length > 0;
    const importStatusIconRef = useRef<SVGSVGElement | null>(null);

    return (
        <div className='w-[100px] @4xl/datasources:w-[120px] relative' key={id}>
            <button
                className={cn(
                    'flex items-center w-full text-textSecondary px-5 pb-2 pt-4 @4xl/datasources:pt-[18px] @4xl/datasources:pb-4 gap-2 shrink-0 rounded-md flex-col bg-tileEditorDataset focus:text-textPrimary',
                    {
                        'hover:bg-tileEditorDatasetActive focus:bg-tileEditorDatasetActive hover:text-textPrimary':
                            !disabled,
                        'opacity-50': disabled
                    }
                )}
                onClick={() => !disabled && selectDatasource()}
                data-testid={`splashDatasourceBtn-${name}`}
                disabled={disabled}
            >
                <div className='w-full max-w-14 aspect-square @4xl/datasources:max-w-16 relative'>
                    <PluginIcon pluginName={name} className='w-full' />
                </div>

                <Text.H4 className='text-sm @4xl/datasources:text-[14px] gap-x-2 text-center min-h-[35px] flex items-center'>
                    {name}
                    {tooltipText && (
                        <Tooltip title={tooltipText} className='text-textSecondary hover:text-textPrimary'>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </Tooltip>
                    )}
                </Text.H4>
            </button>
            {disabled && (
                <Tooltip
                    title={<SplashScreenDatasourceErrorText failedImports={failedImports} />}
                    placement='top-start'
                    className='absolute inset-0'
                    offsetOptions={5}
                    referenceElement={importStatusIconRef.current}
                >
                    <FontAwesomeIcon
                        icon={faXmarkCircle}
                        className='size-[18px] block bg-splashScreenIconErrorBorder text-statusErrorPrimary border-transparent border-[2px] rounded-full right-5 top-[5px] absolute  @4xl/datasources:size-[22px]  @4xl/datasources:border-[3px] @4xl/datasources:top-[6px] @4xl/datasources:right-6'
                        ref={importStatusIconRef}
                    />
                </Tooltip>
            )}
        </div>
    );
};
